/**
 * Wizard Step 1 - Configuration
 *
 * Choose inventory type, categories, and products.
 * @see US-12.7 - Step 1: Configuration
 */

import { useState, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ClipboardList, Layers, ListChecks, List, X, Info } from 'lucide-react';
import { Button, Input, Textarea, Checkbox, Skeleton, SearchAutocomplete, DatePicker, Select } from '@/components/ui';
import type { DateRange } from '@/components/ui';
import type { SearchAutocompleteOption } from '@/components/ui';
import { apiClient } from '@/api/client';
import { useDebounce, useBackdatingLimit, useCutoffHour, useCurrentRole } from '@/hooks';
import { formatDateParam } from '@/components/ui';
import { UserRole, hasMinimumRole } from '@bstock/shared';
import type { CreateInventoryDto, RawProductCategory, ApiSuccess, RawProductWithRelations, PaginatedResult } from '@bstock/shared';
import { InventoryType, INVENTORY_TYPE_LABELS } from '@bstock/shared';
import { useActiveInventoryLists } from '../../../inventory-lists';

interface WizardStep1Props {
  onSubmit: (data: CreateInventoryDto) => Promise<void>;
  isLoading: boolean;
}

// Fetch categories
async function fetchCategories() {
  const response = await apiClient.get<ApiSuccess<RawProductCategory[]>>('/raw-product-categories');
  return response.data.data;
}

// Fetch raw products for search
async function searchRawProducts(search: string) {
  const response = await apiClient.get<ApiSuccess<PaginatedResult<RawProductWithRelations>>>('/raw-products', {
    params: { search, limit: 20 },
  });
  return response.data.data;
}

// Selected product info (minimal data stored)
interface SelectedProduct {
  id: string;
  name: string;
  sku: string;
}

const typeOptions = [
  {
    value: InventoryType.FULL,
    label: INVENTORY_TYPE_LABELS[InventoryType.FULL],
    description: 'Inventaire de tous les produits actifs',
    icon: ClipboardList,
  },
  {
    value: InventoryType.BY_CATEGORY,
    label: INVENTORY_TYPE_LABELS[InventoryType.BY_CATEGORY],
    description: 'Selectionner une ou plusieurs categories',
    icon: Layers,
  },
  {
    value: InventoryType.BY_SELECTION,
    label: INVENTORY_TYPE_LABELS[InventoryType.BY_SELECTION],
    description: 'Choisir des produits specifiques',
    icon: ListChecks,
  },
  {
    value: InventoryType.BY_LIST,
    label: INVENTORY_TYPE_LABELS[InventoryType.BY_LIST],
    description: 'Utiliser une liste pre-configuree',
    icon: List,
  },
];

export function WizardStep1({ onSubmit, isLoading }: WizardStep1Props) {
  const { minDate, maxDate } = useBackdatingLimit();
  const cutoffHour = useCutoffHour();
  const currentRole = useCurrentRole();
  // Admin/Owner can see and modify the time; others only see the date
  const isAdminOrOwner = currentRole ? hasMinimumRole(currentRole, UserRole.ADMIN) : false;
  const [inventoryType, setInventoryType] = useState<InventoryType>(InventoryType.FULL);
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [includeZeroStock, setIncludeZeroStock] = useState(true);
  const [effectiveDateRange, setEffectiveDateRange] = useState<DateRange>({ start: null, end: null });

  // Product selection state (for BY_SELECTION)
  const [productSearch, setProductSearch] = useState('');
  const [selectedProducts, setSelectedProducts] = useState<SelectedProduct[]>([]);

  // List selection state (for BY_LIST)
  const [selectedListId, setSelectedListId] = useState<string>('');
  const { data: activeLists } = useActiveInventoryLists();

  // Debounce search input
  const debouncedSearch = useDebounce(productSearch, 300);

  // Get selected product IDs for submission
  const selectedProductIds = useMemo(() => selectedProducts.map((p) => p.id), [selectedProducts]);

  // Fetch categories
  const { data: categories, isLoading: categoriesLoading } = useQuery({
    queryKey: ['raw-product-categories'],
    queryFn: fetchCategories,
  });

  // Fetch raw products for search
  const { data: searchResults, isLoading: searchLoading } = useQuery({
    queryKey: ['raw-products-search', debouncedSearch],
    queryFn: () => searchRawProducts(debouncedSearch),
    enabled: debouncedSearch.length >= 2,
  });

  // Transform search results to SearchAutocomplete options, filtering out selected
  const productOptions = useMemo<SearchAutocompleteOption[]>(() => {
    const selectedIds = new Set(selectedProductIds);
    return (searchResults?.data ?? [])
      .filter((p) => !selectedIds.has(p.id))
      .map((p) => ({
        id: p.id,
        label: p.name,
        sublabel: `SKU: ${p.sku}${p.category ? ` | ${p.category.name}` : ''}`,
      }));
  }, [searchResults?.data, selectedProductIds]);

  // Handle adding a product from autocomplete
  const handleProductSelect = useCallback((option: SearchAutocompleteOption | null) => {
    if (option) {
      const product = searchResults?.data?.find((p) => p.id === option.id);
      if (product) {
        setSelectedProducts((prev) => [
          ...prev,
          { id: product.id, name: product.name, sku: product.sku },
        ]);
      }
    }
  }, [searchResults?.data]);

  // Handle removing a product
  const handleRemoveProduct = useCallback((productId: string) => {
    setSelectedProducts((prev) => prev.filter((p) => p.id !== productId));
  }, []);

  // Handle category toggle
  const handleCategoryToggle = useCallback((categoryId: string) => {
    setSelectedCategoryIds((prev) =>
      prev.includes(categoryId)
        ? prev.filter((id) => id !== categoryId)
        : [...prev, categoryId]
    );
  }, []);

  // Handle form submission
  const handleSubmit = useCallback(async () => {
    const data: CreateInventoryDto = {
      type: inventoryType,
      name: name.trim() || undefined,
      notes: notes.trim() || undefined,
      includeZeroStock,
      effectiveDate: formatDateParam(effectiveDateRange.start) ?? undefined,
    };

    if (inventoryType === InventoryType.BY_CATEGORY) {
      data.categoryIds = selectedCategoryIds;
    }

    if (inventoryType === InventoryType.BY_SELECTION) {
      data.rawProductIds = selectedProductIds;
    }

    if (inventoryType === InventoryType.BY_LIST) {
      data.inventoryListId = selectedListId || undefined;
    }

    await onSubmit(data);
  }, [inventoryType, name, notes, selectedCategoryIds, selectedProductIds, selectedListId, includeZeroStock, effectiveDateRange, onSubmit]);

  const canSubmit =
    inventoryType === InventoryType.FULL ||
    (inventoryType === InventoryType.BY_CATEGORY && selectedCategoryIds.length > 0) ||
    (inventoryType === InventoryType.BY_SELECTION && selectedProductIds.length > 0) ||
    (inventoryType === InventoryType.BY_LIST && !!selectedListId);

  return (
    <div className="space-y-6">
      {/* Inventory type selection */}
      <div>
        <label className="block text-sm font-medium text-neutral-700 mb-3">
          Type d'inventaire
        </label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {typeOptions.map((option) => {
            const Icon = option.icon;
            const isSelected = inventoryType === option.value;

            return (
              <button
                key={option.value}
                type="button"
                onClick={() => setInventoryType(option.value)}
                className={`p-4 rounded-lg border-2 text-left transition-all ${
                  isSelected
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-neutral-200 hover:border-neutral-300'
                }`}
              >
                <div className={`p-2 rounded-lg inline-block mb-2 ${
                  isSelected ? 'bg-primary-100' : 'bg-neutral-100'
                }`}>
                  <Icon className={`w-5 h-5 ${
                    isSelected ? 'text-primary-600' : 'text-neutral-500'
                  }`} />
                </div>
                <div className={`font-medium ${
                  isSelected ? 'text-primary-700' : 'text-dark-500'
                }`}>
                  {option.label}
                </div>
                <div className="text-sm text-neutral-500 mt-1">
                  {option.description}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Category selection (for BY_CATEGORY) */}
      {inventoryType === InventoryType.BY_CATEGORY && (
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-3">
            Categories a inventorier
          </label>
          {categoriesLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-10" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2 max-h-64 overflow-auto p-1">
              {categories?.map((category) => (
                <div
                  key={category.id}
                  onClick={() => handleCategoryToggle(category.id)}
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    selectedCategoryIds.includes(category.id)
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-neutral-200 hover:border-neutral-300'
                  }`}
                >
                  <Checkbox
                    checked={selectedCategoryIds.includes(category.id)}
                    onChange={() => handleCategoryToggle(category.id)}
                  />
                  <div>
                    <div className="font-medium text-dark-500">{category.name}</div>
                    <div className="text-xs text-neutral-500">{category.code}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {selectedCategoryIds.length === 0 && (
            <p className="text-sm text-danger-600 mt-2">
              Selectionnez au moins une categorie
            </p>
          )}
        </div>
      )}

      {/* Product selection (for BY_SELECTION) */}
      {inventoryType === InventoryType.BY_SELECTION && (
        <div>
          <SearchAutocomplete
            label="Produits a inventorier"
            placeholder="Rechercher un produit..."
            value={null}
            onChange={handleProductSelect}
            options={productOptions}
            isLoading={searchLoading}
            searchValue={productSearch}
            onSearchChange={setProductSearch}
            emptyMessage="Aucun produit trouve"
            minSearchLength={2}
            keepFocusOnSelect
          />

          {/* Selected products chips */}
          {selectedProducts.length > 0 ? (
            <div className="mt-3 flex flex-wrap gap-2">
              {selectedProducts.map((product) => (
                <span
                  key={product.id}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary-100 text-primary-700 text-sm"
                >
                  <span className="truncate max-w-[200px]">{product.name}</span>
                  <span className="text-primary-500 text-xs">({product.sku})</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveProduct(product.id)}
                    className="p-0.5 hover:bg-primary-200 rounded-full transition-colors"
                    aria-label={`Retirer ${product.name}`}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </span>
              ))}
            </div>
          ) : (
            <p className="text-sm text-danger-600 mt-2">
              Selectionnez au moins un produit
            </p>
          )}

          <p className="text-xs text-neutral-500 mt-2">
            {selectedProducts.length} produit{selectedProducts.length !== 1 ? 's' : ''} selectionne{selectedProducts.length !== 1 ? 's' : ''}
          </p>
        </div>
      )}

      {/* List selection (for BY_LIST) */}
      {inventoryType === InventoryType.BY_LIST && (
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-3">
            Liste predefinie
          </label>
          {!activeLists || activeLists.length === 0 ? (
            <div className="text-sm text-neutral-500 bg-neutral-50 rounded-lg p-4 border border-neutral-200">
              Aucune liste active. Creez des listes dans Parametres &gt; Listes d'inventaire.
            </div>
          ) : (
            <>
              <Select
                value={selectedListId}
                onChange={(value) => setSelectedListId(value)}
                placeholder="Selectionner une liste..."
                options={activeLists.map((list) => ({
                  value: list.id,
                  label: `${list.name} (${list.itemCount} produits)`,
                }))}
              />
              {!selectedListId && (
                <p className="text-sm text-danger-600 mt-2">
                  Selectionnez une liste
                </p>
              )}
            </>
          )}
        </div>
      )}

      {/* Cutoff hour info */}
      <div className="flex items-start gap-2 p-2.5 bg-primary-50 border border-primary-100 rounded-lg">
        <Info className="w-4 h-4 text-primary-500 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-primary-700">
          Heure de coupure du site : <strong>{cutoffHour}h00</strong>.
          {effectiveDateRange.start
            ? ` L'inventaire sera daté au ${effectiveDateRange.start.toLocaleDateString('fr-FR')} à ${cutoffHour}h00.`
            : ` Si aucune date n'est spécifiée, l'inventaire sera daté à aujourd'hui ${cutoffHour}h00.`
          }
        </p>
      </div>

      {/* Name and Effective date - side by side */}
      <div className="grid grid-cols-2 gap-4">
        <Input
          label="Nom (optionnel)"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Ex: Inventaire mensuel janvier"
          maxLength={200}
        />
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1">
            Date d'effet (optionnel)
          </label>
          <DatePicker
            asSingle
            showTime={isAdminOrOwner}
            minDate={minDate}
            maxDate={maxDate}
            value={effectiveDateRange}
            onChange={(range) => {
              if (isAdminOrOwner && range.start && range.start.getHours() === 0 && range.start.getMinutes() === 0) {
                const adjusted = new Date(range.start);
                adjusted.setHours(cutoffHour, 0, 0, 0);
                setEffectiveDateRange({ start: adjusted, end: adjusted });
              } else {
                setEffectiveDateRange(range);
              }
            }}
            placeholder="Par defaut: date de validation"
          />
        </div>
      </div>

      {/* Notes */}
      <Textarea
        label="Notes (optionnel)"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Ajouter des notes..."
        rows={4}
        maxLength={2000}
      />

      {/* Include zero stock */}
      <div className="flex items-center gap-3">
        <Checkbox
          checked={includeZeroStock}
          onChange={(e) => setIncludeZeroStock(e.target.checked)}
          label="Inclure les produits avec un stock theorique a zero"
        />
      </div>

      {/* Submit button */}
      <div className="pt-4">
        <Button
          onClick={handleSubmit}
          disabled={!canSubmit}
          isLoading={isLoading}
          className="w-full"
        >
          Creer l'inventaire
        </Button>
      </div>
    </div>
  );
}
